import { useCallback, useEffect, useState } from 'react';
import { dispatch, useSelector } from 'store';
import { useBaseApiLinksQuery } from 'store/_Api/Auth';
import { setLinks as setLinksInSlice } from './BaseLinksSlice';

type Return = {
  getLink: (rel: string) => string | null;
  refetch: () => void;
  selectedSiteUuid?: string;
};

/**
 * Hook that stores the base links. Har auto-loading of links using the useBaseApiLinksQuery,
 * and provides functionality for:
 * - getting link by given rel
 * - adding new links
 * - removing links
 */
const useBaseLinks = (): Return => {
  const { map, selectedSiteUuid } = useSelector((state) => state.baseLinks);

  // If we already have a map, it means the request was already made,
  // so we just skip it
  const [skip, setSkip] = useState(!!map);
  const {
    data: response,
    refetch: reload,
    isUninitialized,
  } = useBaseApiLinksQuery(undefined, {
    skip,
  });

  const reset = () => {
    if (isUninitialized === false) {
      setSkip(false);
      reload();
    }
  };

  useEffect(() => {
    /**
     * When we get a response, we set the links state to the received links
     */
    if (response) {
      const appLinks = response.links;
      if (appLinks) {
        dispatch(setLinksInSlice(appLinks ?? []));
      }
    }
  }, [response]);

  /**
   * Returns link by given rel from the map,
   * provided that is exists
   */
  const getLink = useCallback(
    (rel: string): string | null => {
      if (!map) {
        return null;
      }
      return map[rel] ?? null;
    },
    [map],
  );

  return {
    getLink,
    refetch: reset,
    selectedSiteUuid,
  };
};

export default useBaseLinks;
